import {
  CASH_DEPOSIT_AMD,
  LAST_MINUTE_DISCOUNT_PERCENT,
  LAST_MINUTE_WINDOW_HOURS,
} from "./config";

export type PaymentMethod = "CARD" | "IDRAM" | "CASH";

/** Admin-tunable business rules. Defaults to the env-configured values. */
export interface PricingSettings {
  cashDepositAmd: number;
  lastMinuteDiscountPercent: number;
  lastMinuteWindowHours: number;
}

const DEFAULT_SETTINGS: PricingSettings = {
  cashDepositAmd: CASH_DEPOSIT_AMD,
  lastMinuteDiscountPercent: LAST_MINUTE_DISCOUNT_PERCENT,
  lastMinuteWindowHours: LAST_MINUTE_WINDOW_HOURS,
};

export interface QuoteInput {
  pricePerNight: number;
  checkIn: Date;
  checkOut: Date;
  paymentMethod: PaymentMethod;
  now?: Date;
  /** Editable settings; falls back to env defaults when omitted. */
  settings?: PricingSettings;
  /** Guest opted into the last-minute deal (required for the discount). */
  lastMinute?: boolean;
  /**
   * Price for each night of the stay (per-day overrides). When provided and the
   * length matches the night count, the subtotal sums these instead of using a
   * flat pricePerNight.
   */
  nightlyPrices?: number[];
}

export interface Quote {
  nights: number;
  pricePerNight: number;
  subtotal: number;
  isLastMinute: boolean;
  discountPercent: number;
  discountAmount: number;
  total: number;
  /** Non-refundable deposit (cash bookings only). */
  depositAmount: number;
  /** Amount charged online now (deposit for cash, full total otherwise). */
  amountDueNow: number;
}

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/** Whole nights between two dates (date-only, ignores time). */
export function nightsBetween(checkIn: Date, checkOut: Date): number {
  const a = Date.UTC(
    checkIn.getFullYear(),
    checkIn.getMonth(),
    checkIn.getDate(),
  );
  const b = Date.UTC(
    checkOut.getFullYear(),
    checkOut.getMonth(),
    checkOut.getDate(),
  );
  return Math.round((b - a) / MS_PER_DAY);
}

/** True when check-in is within the last-minute window from `now`. */
export function isWithinLastMinuteWindow(
  checkIn: Date,
  now: Date = new Date(),
  windowHours: number = LAST_MINUTE_WINDOW_HOURS,
): boolean {
  const diffHours = (checkIn.getTime() - now.getTime()) / (1000 * 60 * 60);
  // Date-only check-ins sit at midnight, so "today" can be a few hours in the
  // past — still count it (and anything up to the window ahead) as last-minute.
  return diffHours <= windowHours && diffHours > -24;
}

/**
 * Computes the price breakdown for a booking, applying the business rules:
 *  - CASH: non-refundable deposit charged online now, rest paid on arrival.
 *  - Last-minute (online only): 15% off, full amount charged online now,
 *    non-refundable.
 *  - Normal online booking: full amount charged online after approval.
 */
export function quoteBooking(input: QuoteInput): Quote {
  const { pricePerNight, checkIn, checkOut, paymentMethod } = input;
  const now = input.now ?? new Date();
  const settings = input.settings ?? DEFAULT_SETTINGS;

  const nights = Math.max(0, nightsBetween(checkIn, checkOut));
  const subtotal =
    input.nightlyPrices && input.nightlyPrices.length === nights
      ? input.nightlyPrices.reduce((a, b) => a + b, 0)
      : nights * pricePerNight;

  const online = paymentMethod === "CARD" || paymentMethod === "IDRAM";
  // The last-minute discount requires an explicit opt-in, an online payment,
  // and the booking being inside the last-minute window.
  const isLastMinute =
    (input.lastMinute ?? false) &&
    online &&
    isWithinLastMinuteWindow(checkIn, now, settings.lastMinuteWindowHours);

  const discountPercent = isLastMinute
    ? settings.lastMinuteDiscountPercent
    : 0;
  const discountAmount = Math.round((subtotal * discountPercent) / 100);
  const total = subtotal - discountAmount;

  let depositAmount = 0;
  let amountDueNow = 0;

  if (paymentMethod === "CASH") {
    depositAmount = settings.cashDepositAmd;
    amountDueNow = settings.cashDepositAmd;
  } else {
    // Online: charge the full (possibly discounted) total.
    amountDueNow = total;
  }

  return {
    nights,
    pricePerNight,
    subtotal,
    isLastMinute,
    discountPercent,
    discountAmount,
    total,
    depositAmount,
    amountDueNow,
  };
}

/**
 * Cancellation fee kept when a GUEST cancels a paid online booking (uniform
 * across payment methods and dates; admin-initiated cancellations refund 100%).
 *
 * fee = max(deposit, feePercent% of the paid amount), capped at the paid
 * amount. The percentage floor (admin-configurable, default 5%) covers the
 * ~2.7% gateway commission (which the bank keeps even when we refund) with
 * margin on large bookings; small bookings keep the flat deposit. Refund owed
 * to the guest = paid − fee.
 */
export const CANCEL_FEE_PERCENT = 5;

export function guestCancellationFee(
  paidAmd: number,
  depositAmd: number,
  feePercent: number = CANCEL_FEE_PERCENT,
): number {
  const pct = Math.ceil((paidAmd * feePercent) / 100);
  return Math.min(paidAmd, Math.max(depositAmd, pct));
}

/**
 * Price for each night in [checkIn, checkOut): a per-day override when present,
 * otherwise the default. Keys are YYYY-MM-DD in UTC (matching the calendar).
 */
export function nightlyPrices(
  checkIn: Date,
  checkOut: Date,
  defaultPrice: number,
  overrides: Record<string, number> = {},
): number[] {
  const out: number[] = [];
  const d = new Date(checkIn);
  d.setUTCHours(0, 0, 0, 0);
  const end = new Date(checkOut);
  end.setUTCHours(0, 0, 0, 0);
  while (d < end) {
    const key = d.toISOString().slice(0, 10);
    out.push(overrides[key] ?? defaultPrice);
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}
